import { useEffect, useState } from "react";
import { toast } from "sonner";
import { CreditCard, Mail, AlertTriangle, CheckCircle2, Loader2, Eye, EyeOff } from "lucide-react";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Switch } from "../../components/ui/switch";
import { Checkbox } from "../../components/ui/checkbox";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../../components/ui/select";
import { api } from "../../lib/api";
import { apiError } from "../../lib/api";

const PAYMENT_METHODS = ["card", "upi", "netbanking", "wallet"];

export default function AdminIntegrations() {
  const [data, setData] = useState(null);
  const [stripeForm, setStripeForm] = useState({
    publishable_key: "", secret_key: "", webhook_secret: "",
    is_enabled: false, mode: "test", payment_methods_enabled: ["card"],
  });
  const [sgForm, setSgForm] = useState({
    api_key: "", from_email: "", from_name: "MediBooks", is_enabled: false,
  });
  const [showSecret, setShowSecret] = useState({ stripe: false, webhook: false, sg: false });
  const [savingStripe, setSavingStripe] = useState(false);
  const [savingSg, setSavingSg] = useState(false);
  const [testingStripe, setTestingStripe] = useState(false);
  const [testingSg, setTestingSg] = useState(false);

  const load = () =>
    api.get("/admin/integrations").then((r) => {
      setData(r.data);
      setStripeForm({
        publishable_key: r.data.stripe.publishable_key || "",
        secret_key: "",
        webhook_secret: "",
        is_enabled: !!r.data.stripe.is_enabled,
        mode: r.data.stripe.mode || "test",
        payment_methods_enabled: r.data.stripe.payment_methods_enabled || ["card"],
      });
      setSgForm({
        api_key: "",
        from_email: r.data.sendgrid.from_email || "",
        from_name: r.data.sendgrid.from_name || "MediBooks",
        is_enabled: !!r.data.sendgrid.is_enabled,
      });
    });

  useEffect(() => { load(); }, []);

  const saveStripe = async () => {
    setSavingStripe(true);
    try {
      await api.patch("/admin/integrations/stripe", stripeForm);
      toast.success("Stripe settings saved");
      await load();
    } catch (e) {
      toast.error(apiError(e, "Save failed"));
    } finally { setSavingStripe(false); }
  };
  const saveSg = async () => {
    setSavingSg(true);
    try {
      await api.patch("/admin/integrations/sendgrid", sgForm);
      toast.success("SendGrid settings saved");
      await load();
    } catch (e) {
      toast.error(apiError(e, "Save failed"));
    } finally { setSavingSg(false); }
  };
  const testStripe = async () => {
    setTestingStripe(true);
    try {
      const r = await api.post("/admin/integrations/stripe/test");
      toast.success(`Stripe accepted the key (mode: ${r.data.mode})`);
    } catch (e) {
      toast.error(apiError(e, "Stripe test failed"));
    } finally { setTestingStripe(false); }
  };
  const testSg = async () => {
    setTestingSg(true);
    try {
      const r = await api.post("/admin/integrations/sendgrid/test");
      toast.success(`Test email sent to ${r.data.delivered_to}`);
    } catch (e) {
      toast.error(apiError(e, "SendGrid test failed"));
    } finally { setTestingSg(false); }
  };

  if (!data) return <div className="text-sm text-mb-fg2">Loading…</div>;

  return (
    <div className="space-y-8 max-w-5xl" data-testid="admin-integrations">
      <div>
        <div className="text-[10px] uppercase tracking-[0.3em] text-teal-400">◖ integrations</div>
        <h1 className="mt-2 text-3xl font-light tracking-tight">Platform integrations</h1>
        <p className="mt-2 text-sm text-mb-fg2 max-w-2xl">
          Hook real Stripe + SendGrid credentials in. Saved keys are encrypted at rest. WhatsApp is configured per-hospital by each hospital admin.
        </p>
      </div>

      <DemoBanner stripe={data.stripe} sendgrid={data.sendgrid} />

      {/* STRIPE */}
      <section className="rounded-xl border border-mb-border bg-mb-surface" data-testid="card-stripe">
        <header className="px-6 py-5 border-b border-mb-border flex items-start justify-between gap-4">
          <div className="flex items-start gap-3">
            <div className="h-10 w-10 rounded-lg bg-teal-500/10 text-teal-300 flex items-center justify-center">
              <CreditCard className="h-5 w-5" />
            </div>
            <div>
              <div className="text-base font-medium">Stripe payments</div>
              <div className="text-xs text-mb-fg2 mt-0.5">
                Stripe needs to be enabled <span className="text-mb-fg2">and</span> configured for real payments. Otherwise demo mode runs.
              </div>
            </div>
          </div>
          <StatusBadge ok={data.stripe.is_configured && data.stripe.is_enabled} label={
            data.stripe.is_enabled
              ? (data.stripe.is_configured ? "Live · " + data.stripe.mode : "Enabled · no key")
              : "Disabled"
          } />
        </header>
        <div className="p-6 grid grid-cols-1 md:grid-cols-2 gap-5">
          <Field label="Mode">
            <Select value={stripeForm.mode} onValueChange={(v) => setStripeForm({ ...stripeForm, mode: v })}>
              <SelectTrigger data-testid="stripe-mode" className="bg-mb-surface2 border-mb-border text-white h-10 rounded-lg">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-mb-surface2 border-mb-border text-white">
                <SelectItem value="test">Test mode</SelectItem>
                <SelectItem value="live">Live mode</SelectItem>
              </SelectContent>
            </Select>
          </Field>
          <Field label="Enable Stripe">
            <div className="flex items-center gap-3 h-10">
              <Switch checked={stripeForm.is_enabled}
                onCheckedChange={(v) => setStripeForm({ ...stripeForm, is_enabled: v })}
                data-testid="stripe-enabled" />
              <span className="text-xs text-mb-fg2">{stripeForm.is_enabled ? "On" : "Off"}</span>
            </div>
          </Field>

          <Field label="Publishable key (public)">
            <Input value={stripeForm.publishable_key}
              onChange={(e) => setStripeForm({ ...stripeForm, publishable_key: e.target.value })}
              placeholder="pk_test_…" data-testid="stripe-publishable"
              className="bg-mb-surface2 border-mb-border text-white h-10 rounded-lg" />
          </Field>

          <MaskedField
            label="Secret key"
            current={data.stripe.secret_key_masked}
            visible={showSecret.stripe}
            onToggle={() => setShowSecret({ ...showSecret, stripe: !showSecret.stripe })}
            value={stripeForm.secret_key}
            onChange={(v) => setStripeForm({ ...stripeForm, secret_key: v })}
            placeholder="sk_test_…"
            testid="stripe-secret"
          />

          <MaskedField
            label="Webhook secret"
            current={data.stripe.webhook_secret_masked}
            visible={showSecret.webhook}
            onToggle={() => setShowSecret({ ...showSecret, webhook: !showSecret.webhook })}
            value={stripeForm.webhook_secret}
            onChange={(v) => setStripeForm({ ...stripeForm, webhook_secret: v })}
            placeholder="whsec_…"
            testid="stripe-webhook"
          />

          <Field label="Payment methods" full>
            <div className="flex flex-wrap gap-3 pt-1">
              {PAYMENT_METHODS.map((m) => (
                <label key={m} className="inline-flex items-center gap-2 px-3 py-2 rounded-lg border border-mb-border bg-mb-surface2 text-sm capitalize">
                  <Checkbox
                    checked={stripeForm.payment_methods_enabled.includes(m)}
                    onCheckedChange={(v) => {
                      const next = v
                        ? Array.from(new Set([...stripeForm.payment_methods_enabled, m]))
                        : stripeForm.payment_methods_enabled.filter((x) => x !== m);
                      setStripeForm({ ...stripeForm, payment_methods_enabled: next });
                    }}
                    data-testid={`stripe-method-${m}`}
                  />
                  <span className="text-mb-fg2">{m}</span>
                </label>
              ))}
            </div>
          </Field>
        </div>
        <footer className="px-6 py-4 border-t border-mb-border flex items-center gap-3 justify-end">
          <Button
            variant="outline" data-testid="stripe-test"
            disabled={testingStripe || !data.stripe.is_configured || !data.stripe.is_enabled}
            onClick={testStripe}
            className="bg-transparent border-mb-border hover:bg-mb-surface2 text-white">
            {testingStripe ? <Loader2 className="h-4 w-4 animate-spin" /> : "Test connection"}
          </Button>
          <Button
            onClick={saveStripe} disabled={savingStripe} data-testid="stripe-save"
            className="bg-teal-500 hover:bg-teal-400 text-zinc-950">
            {savingStripe ? "Saving…" : "Save Stripe"}
          </Button>
        </footer>
      </section>

      {/* SENDGRID */}
      <section className="rounded-xl border border-mb-border bg-mb-surface" data-testid="card-sendgrid">
        <header className="px-6 py-5 border-b border-mb-border flex items-start justify-between gap-4">
          <div className="flex items-start gap-3">
            <div className="h-10 w-10 rounded-lg bg-teal-500/10 text-teal-300 flex items-center justify-center">
              <Mail className="h-5 w-5" />
            </div>
            <div>
              <div className="text-base font-medium">SendGrid email</div>
              <div className="text-xs text-mb-fg2 mt-0.5">
                Used for transactional notifications (booking, payment, cancellation). Verified sender required.
              </div>
            </div>
          </div>
          <StatusBadge ok={data.sendgrid.is_configured && data.sendgrid.is_enabled} label={
            data.sendgrid.is_enabled
              ? (data.sendgrid.is_configured ? "Live" : "Enabled · no key")
              : "Disabled"
          } />
        </header>
        <div className="p-6 grid grid-cols-1 md:grid-cols-2 gap-5">
          <MaskedField
            label="API key"
            current={data.sendgrid.api_key_masked}
            visible={showSecret.sg}
            onToggle={() => setShowSecret({ ...showSecret, sg: !showSecret.sg })}
            value={sgForm.api_key}
            onChange={(v) => setSgForm({ ...sgForm, api_key: v })}
            placeholder="SG.xxxxxxxx"
            testid="sg-api-key"
          />
          <Field label="From email">
            <Input value={sgForm.from_email}
              onChange={(e) => setSgForm({ ...sgForm, from_email: e.target.value })}
              placeholder="noreply@yourdomain.com" data-testid="sg-from-email"
              className="bg-mb-surface2 border-mb-border text-white h-10 rounded-lg" />
          </Field>
          <Field label="From name">
            <Input value={sgForm.from_name}
              onChange={(e) => setSgForm({ ...sgForm, from_name: e.target.value })}
              data-testid="sg-from-name"
              className="bg-mb-surface2 border-mb-border text-white h-10 rounded-lg" />
          </Field>
          <Field label="Enable SendGrid">
            <div className="flex items-center gap-3 h-10">
              <Switch checked={sgForm.is_enabled}
                onCheckedChange={(v) => setSgForm({ ...sgForm, is_enabled: v })}
                data-testid="sg-enabled" />
              <span className="text-xs text-mb-fg2">{sgForm.is_enabled ? "On" : "Off"}</span>
            </div>
          </Field>
        </div>
        <footer className="px-6 py-4 border-t border-mb-border flex items-center gap-3 justify-end">
          <Button
            variant="outline" data-testid="sg-test"
            disabled={testingSg || !data.sendgrid.is_configured || !data.sendgrid.is_enabled}
            onClick={testSg}
            className="bg-transparent border-mb-border hover:bg-mb-surface2 text-white">
            {testingSg ? <Loader2 className="h-4 w-4 animate-spin" /> : "Send test email"}
          </Button>
          <Button
            onClick={saveSg} disabled={savingSg} data-testid="sg-save"
            className="bg-teal-500 hover:bg-teal-400 text-zinc-950">
            {savingSg ? "Saving…" : "Save SendGrid"}
          </Button>
        </footer>
      </section>
    </div>
  );
}

function DemoBanner({ stripe, sendgrid }) {
  const payOk = stripe.is_enabled && stripe.is_configured;
  const mailOk = sendgrid.is_enabled && sendgrid.is_configured;
  if (payOk && mailOk) {
    return (
      <div data-testid="demo-banner" className="rounded-xl border border-teal-500/20 bg-teal-500/5 px-5 py-4 flex items-center gap-3">
        <CheckCircle2 className="h-5 w-5 text-teal-300" />
        <div className="text-sm text-mb-fg2">All platform integrations are live.</div>
      </div>
    );
  }
  return (
    <div data-testid="demo-banner" className="rounded-xl border border-amber-500/20 bg-amber-500/5 px-5 py-4 flex items-start gap-3">
      <AlertTriangle className="h-5 w-5 text-amber-300 mt-0.5" />
      <div className="text-sm text-mb-fg2">
        <div className="font-medium text-amber-200">Demo mode is active for some channels</div>
        <div className="mt-1 text-mb-fg2">
          Payments: <span className={payOk ? "text-teal-300" : "text-amber-300"}>{payOk ? "Stripe live" : "MOCK"}</span>
          {" · "}
          Email: <span className={mailOk ? "text-teal-300" : "text-amber-300"}>{mailOk ? "SendGrid live" : "MOCK"}</span>
          {" · "}
          WhatsApp: <span className="text-mb-fg2">per-hospital</span>
        </div>
      </div>
    </div>
  );
}

function StatusBadge({ ok, label }) {
  return (
    <span className={`text-[10px] uppercase tracking-[0.2em] px-2.5 py-1 rounded border ${
      ok ? "bg-teal-500/10 text-teal-300 border-teal-500/20"
         : "bg-zinc-800/60 text-mb-fg2 border-mb-border"
    }`}>{label}</span>
  );
}

function Field({ label, children, full }) {
  return (
    <div className={full ? "md:col-span-2" : ""}>
      <Label className="text-[10px] uppercase tracking-[0.2em] text-mb-fg2">{label}</Label>
      <div className="mt-2">{children}</div>
    </div>
  );
}

function MaskedField({ label, current, value, onChange, placeholder, testid, visible, onToggle }) {
  const hasCurrent = !!current;
  return (
    <Field label={label}>
      <div className="flex items-stretch gap-2">
        <Input
          type={visible ? "text" : "password"}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={hasCurrent ? `${current} — leave blank to keep` : placeholder}
          data-testid={testid}
          className="bg-mb-surface2 border-mb-border text-white h-10 rounded-lg flex-1"
        />
        <Button type="button" variant="outline" onClick={onToggle}
          className="bg-transparent border-mb-border hover:bg-mb-surface2 text-mb-fg2 h-10 w-10 p-0"
          data-testid={`${testid}-toggle`}>
          {visible ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
        </Button>
      </div>
    </Field>
  );
}
